export type EditDataKeySource =
  | {
      kind: "document";
      documentId: string;
      versionId: string;
      compatibility: "current" | "legacy";
    }
  | {
      kind: "assistant-process";
      assistantProcessId: string;
    }
  | { kind: "unscoped" }
  | { kind: "invalid"; message: string };

const getString = (value: unknown): string =>
  typeof value === "string" ? value : "";

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

export const resolveEditDataKeySource = (args: unknown): EditDataKeySource => {
  if (!isObject(args)) {
    return {
      kind: "invalid",
      message:
        "Previous value unavailable because the edit arguments are invalid.",
    };
  }
  const legacyDocumentId = getString(args.document_id);
  const legacyVersionId = getString(args.version_id);
  const hasLegacyDocumentSource = !!legacyDocumentId || !!legacyVersionId;

  if (args.source !== undefined && args.source !== null) {
    if (!isObject(args.source)) {
      return {
        kind: "invalid",
        message:
          "Previous value unavailable because the edit source is invalid.",
      };
    }

    const documentId = getString(args.source.document_id);
    const versionId = getString(args.source.version_id);
    const assistantProcessId = getString(args.source.assistant_process_id);
    const hasDocumentSource = !!documentId || !!versionId;

    if (
      (hasDocumentSource && (!documentId || !versionId)) ||
      (hasDocumentSource && !!assistantProcessId) ||
      (!hasDocumentSource && !assistantProcessId)
    ) {
      return {
        kind: "invalid",
        message:
          "Previous value unavailable because the edit source is incomplete or ambiguous.",
      };
    }

    if (hasDocumentSource) {
      if (
        hasLegacyDocumentSource &&
        (legacyDocumentId !== documentId || legacyVersionId !== versionId)
      ) {
        return {
          kind: "invalid",
          message:
            "Previous value unavailable because the record targets disagree.",
        };
      }

      return {
        kind: "document",
        documentId,
        versionId,
        compatibility: "current",
      };
    }

    if (hasLegacyDocumentSource) {
      return {
        kind: "invalid",
        message:
          "Previous value unavailable because the edit has conflicting source types.",
      };
    }

    return {
      kind: "assistant-process",
      assistantProcessId,
    };
  }

  if (hasLegacyDocumentSource) {
    if (!legacyDocumentId || !legacyVersionId) {
      return {
        kind: "invalid",
        message:
          "Previous value unavailable because the legacy record target is incomplete.",
      };
    }

    return {
      kind: "document",
      documentId: legacyDocumentId,
      versionId: legacyVersionId,
      compatibility: "legacy",
    };
  }

  return { kind: "unscoped" };
};
